import { HeartPulse, Activity, GraduationCap, Award } from "lucide-react";
import { DOCTORS, FAQS } from "@/data/site";
import { Reveal, StaggerGroup, StaggerItem } from "@/components/motion/Reveal";
import PageHero from "@/components/PageHero";
import FAQSection from "@/components/FAQSection";
import CTASection from "@/components/CTASection";
import EditorialMarquee from "@/components/EditorialMarquee";

function DoctorProfile({ doc, index }) {
  const flip = index % 2 === 1;
  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center" data-testid={`team-doctor-${index}`}>
      {/* Portrait */}
      <div className={`lg:col-span-5 ${flip ? "lg:order-2" : ""}`}>
        <Reveal>
          <div className="relative aspect-[4/5] rounded-[2rem] overflow-hidden border border-line bg-mint group max-w-md mx-auto">
            <div className="absolute inset-0 z-10 bg-[radial-gradient(120%_80%_at_50%_0%,rgba(46,139,62,0.14),transparent_60%)]" />
            <img
              src={doc.photo}
              alt={doc.name}
              className="absolute inset-0 w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
            />
          </div>
        </Reveal>
      </div>

      {/* Details */}
      <div className={`lg:col-span-7 ${flip ? "lg:order-1" : ""}`}>
        <Reveal>
          <span className="inline-flex items-center gap-2 rounded-full bg-mint text-brand px-4 py-2 text-[11px] uppercase tracking-[0.2em] font-semibold mb-6">
            {index === 0 ? <HeartPulse size={13} /> : <Activity size={13} />} {doc.subrole}
          </span>
          <h2 className="font-display text-4xl md:text-6xl leading-[0.95] tracking-tight text-ink font-light mb-4">{doc.name}</h2>
          <p className="text-brand font-semibold text-lg mb-6">{doc.role}</p>
          <div className="inline-flex items-baseline gap-3 rounded-2xl bg-mint border border-line px-6 py-4 mb-8">
            <span className="font-display text-4xl md:text-5xl text-brand leading-none">{doc.experience}</span>
            <span className="text-sm text-sage font-medium">Years of<br />Expertise</span>
          </div>
        </Reveal>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          <Reveal>
            <div className="rounded-3xl border border-line bg-surface p-7 h-full">
              <GraduationCap size={22} className="text-brand mb-4" />
              <h4 className="font-display text-xl text-ink mb-3">Education</h4>
              <ul className="space-y-2.5">
                {doc.degrees.map((d) => (
                  <li key={d} className="text-sm text-sage leading-relaxed border-b border-line pb-2.5 last:border-0">{d}</li>
                ))}
              </ul>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="rounded-3xl border border-line bg-surface p-7 h-full">
              <Award size={22} className="text-brand mb-4" />
              <h4 className="font-display text-xl text-ink mb-3">Positions & Memberships</h4>
              <ul className="space-y-2.5">
                {[...doc.positions, ...doc.memberships].map((d) => (
                  <li key={d} className="text-sm text-sage leading-relaxed border-b border-line pb-2.5 last:border-0">{d}</li>
                ))}
              </ul>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.15}>
          <div className="mt-6 flex flex-wrap gap-2">
            {doc.expertise.map((e) => (
              <span key={e} className="rounded-full border border-line bg-paper px-4 py-2 text-xs text-ink font-medium">{e}</span>
            ))}
          </div>
        </Reveal>
      </div>
    </div>
  );
}

export default function Team() {
  return (
    <>
      <PageHero
        crumb="Our Team"
        eyebrow="Our Team"
        titleLines={["Expert Healthcare", "for Heart & Metabolic", "Care in Varanasi"]}
        subtitle="Dedicated professionals committed to your holistic, coordinated well-being."
      />

      <section className="pb-24 md:pb-32 bg-paper space-y-24 md:space-y-32" data-testid="team-profiles">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 space-y-24 md:space-y-32">
          <DoctorProfile doc={DOCTORS.pallavi} index={0} />
          <DoctorProfile doc={DOCTORS.ashutosh} index={1} />
        </div>
      </section>

      <EditorialMarquee items={["Heart Care", "Metabolic Care", "Diabetes", "Preventive Cardiology", "Endocrinology", "Coordinated Treatment"]} />

      {/* Collaborative care */}
      <section className="py-24 md:py-32 bg-brand-deep text-white grain relative overflow-hidden" data-testid="team-collaborative">
        <div className="relative max-w-[1400px] mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <Reveal>
              <p className="text-xs uppercase tracking-[0.22em] font-bold text-brand mb-5">Collaborative Heart & Metabolic Care</p>
              <h2 className="font-display text-4xl md:text-5xl font-light leading-tight text-balance">
                Two specialists, one coordinated plan.
              </h2>
            </Reveal>
          </div>
          <div className="lg:col-span-7 space-y-6">
            <Reveal delay={0.1}>
              <p className="text-lg text-mint/85 leading-relaxed">
                Cardiac and metabolic health are deeply intertwined — diabetes, cholesterol and weight all shape the heart. Dr. Pallavi Mishra and Dr. Ashutosh Mishra work together to treat the whole picture, not isolated symptoms.
              </p>
              <p className="text-lg text-mint/85 leading-relaxed">
                This multidisciplinary approach means your care is comprehensive, coordinated and genuinely patient-centered — with every decision made in the full context of your health.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      <FAQSection items={FAQS.team} heading="Our Team — Your Questions" />
      <CTASection title="Schedule Your Consultation with Our Team" />
    </>
  );
}
